<template>
  <div
    ref="headerRef"
    class="HeaderPrimary"
  >
    <div class="grid">
      <div class="header-inner col-1-1 flex justify-space-between align-stretch">
        <UILinkComponent
          :to="homeUrl"
          class="home-logo"
          aria-label="Home"
          aria-current="page"
        >
          <img
            v-if="lowStimulusActive"
            src="~/assets/logo.svg"
            alt="Logo Speeldoos Vught"
            class="header-image"
          />
          <div
            v-else
            ref="logoAnimationRef"
            class="header-image logo-motion"
            role="img"
            aria-hidden="true"
          />
        </UILinkComponent>

        <UIHeaderNavWrapper />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import lottie, { type AnimationItem } from 'lottie-web'
  import logoAnimationData from '~/assets/SpeeldoosVught_LogoMotion_lottie.json'

  const localization = useBaseLocalization()
  const currentLanguage = computed(() => localization.getCurrentLanguage())
  const isMultiLanguage = ref(localization.isMultiLanguage())
  const headerRef = ref<HTMLElement | null>(null)
  const logoAnimationRef = ref<HTMLElement | null>(null)
  let resizeObserver: ResizeObserver | null = null
  let logoAnimation: AnimationItem | null = null

  const { lowStimulusActive } = useBaseAccessibility()

  const initLogoAnimation = () => {
    if (logoAnimation || !logoAnimationRef.value) return
    logoAnimation = lottie.loadAnimation({
      container: logoAnimationRef.value,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: logoAnimationData,
    })
  }

  const destroyLogoAnimation = () => {
    logoAnimation?.destroy()
    logoAnimation = null
  }

  onMounted(() => {
    if (!headerRef.value) return

    resizeObserver = new ResizeObserver(() => {
      if (!headerRef.value) return
      document.documentElement.style.setProperty('--header-height', `${headerRef.value.offsetHeight}px`)
    })
    resizeObserver.observe(headerRef.value)

    if (!lowStimulusActive.value) {
      initLogoAnimation()
    }

    watch(lowStimulusActive, async (isLow) => {
      if (isLow) {
        destroyLogoAnimation()
      } else {
        await nextTick()
        initLogoAnimation()
      }
    })
  })

  onUnmounted(() => {
    resizeObserver?.disconnect()
    destroyLogoAnimation()
  })

  const homeUrl = computed(() => {
    if (!isMultiLanguage.value) {
      return '/'
    }

    if (currentLanguage.value.name == localization.getDefaultLanguage().name) {
      return '/'
    }

    return `/${currentLanguage.value.name}`
  })
</script>

<style lang="scss" scoped>
  .HeaderPrimary {
    position: relative;
    background-color: $white;

    .header-inner {
      position: relative;
    }

    .home-logo {
      position: relative;
      display: flex;
      width: clamp(12.4rem, calc(8.286rem + 8.571vw), 22rem);
      height: auto;

      .header-image {
        width: 100%;
        height: auto;
      }

      .logo-motion {
        aspect-ratio: 875 / 220;
      }
    }
  }

  @include media(sm) {
    .HeaderPrimary {
      .header-inner {
        padding: 2.1rem $gutter;
      }
      .home-logo {
        width: 12.4rem;
      }
    }
  }
</style>
